<template>
  <BaseModal :modalTitle="$t('W: Tilføj bilag')">
    <div class="flex flex-col gap-4">
      <UiPillNav
        :navItems="pillItems"
        @nav-item-click="onPillClick" />

      <div v-if="activeTab === 'library'">
        <div class="mb-2 flex gap-2">
          <input
            v-model="searchQuery"
            type="text"
            class="flex-1 rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-muted focus:border-action focus:ring-1 focus:ring-action"
            :placeholder="$t('W: Søg...')" />
          <select
            v-model="languageFilter"
            class="rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:ring-2 focus:ring-action focus:outline-none">
            <option value="">{{ $t('W: Alle sprog') }}</option>
            <option
              v-for="option in languageOptions"
              :key="option.value"
              :value="option.value">
              {{ option.text }}
            </option>
          </select>
        </div>
        <div class="flex max-h-48 flex-col divide-y divide-border-subtle overflow-y-auto rounded-lg border border-border-subtle">
          <div
            v-if="filteredLibraryItems.length === 0"
            class="p-4 text-center text-sm text-text-muted">
            {{ $t('W: Ingen elementer') }}
          </div>
          <div
            v-for="item in filteredLibraryItems"
            :key="item.id"
            class="flex items-center justify-between gap-2 px-3 py-2.5">
            <span class="truncate text-sm text-text-primary">{{ item.displayTitle }}</span>
            <span
              v-if="addedLibraryItemIDs.includes(item.id)"
              class="flex items-center gap-1.5 text-xs font-medium text-feedback-success">
              <IconLucideCheck class="h-4 w-4" />
              {{ $t('W: Tilføjet') }}
            </span>
            <UiBaseButton
              v-else
              :text="$t('W: Tilføj')"
              theme="primary"
              :isOutlined="true"
              size="sm"
              :isLoading="addingLibraryItemID === item.id"
              @click="onAddFromLibrary(item.id)" />
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'upload'">
        <UiBaseFormVee
          :errorText="uploadErrorText"
          :onSubmit="onUploadSubmit">
          <div class="mb-2">
            <UiFileUpload
              ref="fileUpload"
              :modelValue="uploadFiles"
              :allowedFileTypes="['pdf']"
              :maxFiles="1"
              @update:model-value="onUploadFilesUpdate"
              @on-file-selected="onUploadFileSelected"
              @on-pending-change="onUploadPendingChange" />
          </div>

          <UiInputTextFieldVee
            name="uploadDisplayTitle"
            :label="$t('W: Visningsnavn')"
            :rules="['required']"
            :hint="uploadDisplayTitleHint"
            isRequired
            v-model="uploadForm.displayTitle" />

          <template #buttons>
            <UiBaseButton
              :text="$t('W: Tilføj')"
              :isLoading="isUploading"
              :showSaveIcon="showUploadSaveIcon"
              type="submit" />
          </template>
        </UiBaseFormVee>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useBilagLibraryItemStore } from '@/stores/bilagLibraryItemStore'
import { useOrderStore } from '@/stores/orderStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useToast } from '@/globals/typescript/utils/toast'
import { generateFirestoreId, stripFileExtension } from '@/globals/typescript/utils/utils'
import { getOrderBilagUploadPath } from '@/globals/typescript/utils/orderBilagUploadPath'
import { LAB_CUSTOMER_LANGUAGES } from '@team-milva/lab-models/language'
import type { IFileBase } from '@team-milva/lab-models/files'
import type FileUpload from '@/components/ui/FileUpload.vue'
import type { ITabNavOption } from '@team-milva/lab-components'
import { sanitizeFilenameSegment } from '@team-milva/lab-utils/filename'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'

type TBilagAddTab = 'library' | 'upload'

export default {
  name: 'OrderReportBilagModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    // Blank for a private-segment order, which has no customer account.
    customerAccountID: { type: String, default: '' },
    // The order's owner (MIL-402) — an insurance customer's own order uploads under its prefix.
    customer: { type: Object as PropType<ICustomerRef | null>, default: null },
    tatGroupID: { type: String, required: true },
    reportTemplateID: { type: String, required: true },
    reportLocale: { type: String, required: true },
    alreadyAddedLibraryItemIDs: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),

      activeTab: 'library' as TBilagAddTab,

      searchQuery: '',
      languageFilter: this.reportLocale,
      addingLibraryItemID: '',
      addedLibraryItemIDs: [...this.alreadyAddedLibraryItemIDs] as string[],

      uploadForm: { displayTitle: '' },
      uploadFiles: [] as IFileBase[],
      // FileUpload keeps picked-but-not-yet-uploaded files in its own internal
      // pendingFiles and only pushes to modelValue once uploaded, so a picked
      // file is invisible here without tracking its on-pending-change count.
      pendingUploadCount: 0,
      isUploading: false,
      showUploadSaveIcon: false,
      uploadErrorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useBilagLibraryItemStore, ['allBilagLibraryItems']),

    pillItems(): ITabNavOption[] {
      return [
        {
          id: 'library',
          title: this.$t('W: Fra biblioteket'),
          isActive: this.activeTab === 'library',
        },
        {
          id: 'upload',
          title: this.$t('W: Upload fil'),
          isActive: this.activeTab === 'upload',
        },
      ]
    },

    languageOptions(): { value: string; text: string }[] {
      return Object.values(LAB_CUSTOMER_LANGUAGES)
        .map((lang) => ({ value: lang.locale, text: lang.name }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    filteredLibraryItems() {
      const search = this.searchQuery.trim().toLowerCase()
      return this.allBilagLibraryItems.filter((item) => {
        if (item.activeState !== 'active') return false
        const matchesSearch =
          !search || item.internalTitle.toLowerCase().includes(search)
        const matchesLanguage =
          !this.languageFilter || item.locale === this.languageFilter
        return matchesSearch && matchesLanguage
      })
    },

    uploadDisplayTitleHint(): string {
      if (!this.uploadForm.displayTitle) return ''
      return this.$t('W: Vises som: {name}.pdf', {
        name: sanitizeFilenameSegment(this.uploadForm.displayTitle),
      })
    },
  },

  watch: {
    activeTab(tab: TBilagAddTab) {
      this.resetPendingUploadOnTabChange(tab)
    },
  },

  methods: {
    ...mapActions(useOrderStore, [
      'addOrderReportBilagFromLibrary',
      'addOrderReportBilagUpload',
    ]),

    onPillClick(id: string): void {
      this.activeTab = id as TBilagAddTab
    },

    // `pendingUploadCount` mirrors state that lives inside FileUpload, and the
    // upload tab sits behind a `v-if` — leaving it unmounts the component and
    // takes its picked-but-not-uploaded file with it. Without this the count
    // stayed behind, so the guard warned about a file that no longer existed:
    // pick a file, switch to the library tab, come back, clear the title, and
    // an empty-looking form still refused to close.
    resetPendingUploadOnTabChange(tab: TBilagAddTab): void {
      if (tab !== 'upload') this.pendingUploadCount = 0
    },

    async onAddFromLibrary(bilagLibraryItemID: string): Promise<void> {
      this.addingLibraryItemID = bilagLibraryItemID
      try {
        await this.addOrderReportBilagFromLibrary(
          this.orderID,
          this.tatGroupID,
          this.reportTemplateID,
          bilagLibraryItemID
        )
        this.addedLibraryItemIDs = [
          ...this.addedLibraryItemIDs,
          bilagLibraryItemID,
        ]
        useToast().success(this.$t('W: Bilag tilføjet'))
      } catch {
        useToast().error(this.$t('W: Kunne ikke tilføje bilag'))
      } finally {
        this.addingLibraryItemID = ''
      }
    },

    onUploadFilesUpdate(files: IFileBase[]): void {
      this.uploadFiles = files
      this.uploadErrorText = ''
    },

    onUploadPendingChange(count: number): void {
      this.pendingUploadCount = count
    },

    onUploadFileSelected(file: File): void {
      this.uploadForm.displayTitle = stripFileExtension(file.name)
    },

    async onUploadSubmit(): Promise<void> {
      this.isUploading = true
      this.uploadErrorText = ''
      try {
        const attachmentID = generateFirestoreId()
        const exactUploadPath = getOrderBilagUploadPath({
          labAccountID: this.labAccount?.id ?? '',
          customerAccountID: this.customerAccountID,
          customer: this.customer,
          orderID: this.orderID,
          attachmentID,
        })

        const uploaded = await (
          this.$refs.fileUpload as InstanceType<typeof FileUpload>
        ).uploadPending(exactUploadPath)
        const storagePath = uploaded[0]?.path

        if (!storagePath) {
          this.uploadErrorText = this.$t('W: Vælg en PDF-fil')
          return
        }

        await this.addOrderReportBilagUpload(
          this.orderID,
          this.tatGroupID,
          this.reportTemplateID,
          attachmentID,
          this.uploadForm.displayTitle,
          storagePath
        )
        this.showUploadSaveIcon = true
        setTimeout(() => {
          this.showUploadSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Bilag tilføjet'))
        this.$emit('close')
      } catch {
        this.uploadErrorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke tilføje bilag'))
      } finally {
        this.isUploading = false
      }
    },
  },

  created(): void {
    useBilagLibraryItemStore().getBilagLibraryItems()

    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    //
    // Scoped to the upload tab with an explicit isDirty rather than a snapshot:
    // the library tab's onAddFromLibrary() writes per click and stays open, so
    // it holds nothing to lose and must keep closing silently. That makes this
    // one modal exempt on one tab and draft-holding on the other.
    this.unsavedGuard.register({
      isDirty: () =>
        Boolean(this.uploadForm.displayTitle) || this.pendingUploadCount > 0,
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
